import React from "react";

interface Props {
  courseName: string;
}

const Proof: React.FC<Props> = ({ courseName }) => (
  <section className="proof-section" id="preview" aria-labelledby="proof-title">
    <div className="hero-pattern" aria-hidden="true" style={{ opacity: ".6" }}>
      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <svg x="-5%" y="20%" overflow="visible">
          <use
            href="#blob-2"
            fill="#B798F2"
            transform="scale(1.12)"
            opacity="0.18"
          ></use>
        </svg>
        <svg x="92%" y="60%" overflow="visible">
          <use
            href="#blob-1"
            fill="#FFA5BC"
            transform="scale(1.12)"
            opacity="0.18"
          ></use>
        </svg>
      </svg>
    </div>
    <div className="container">
      <div className="head">
        <h2 className="section-title" id="proof-title">
          How this{" "}
          <span className="nobr">
            course <em className="hl">works</em>.
          </span>
        </h2>
        <p className="section-sub">
          From the first lesson to the certificate on your wall, here's what the
          journey actually looks like.
        </p>
      </div>
      <div className="proof-grid">
        <article className="proof-card">
          <div className="img-wrap">
            <figure
              className="mock mk-scroll"
              role="img"
              aria-label="A 42courses lesson page, scrolling through its video, a story-led written explanation with links, a Three Barriers to Action diagram, a personalised insights panel, and the question you answer at the end."
            >
              <span className="vp" aria-hidden="true">
                <span className="pg">
                  <span className="shero">
                    <i className="seyb">Chapter 2 &nbsp;|&nbsp; Lesson 1</i>
                    <b className="sttl">
                      Why is changing behaviour so difficult?
                    </b>
                    <i className="smeta">⏱ 20 min</i>
                  </span>
                  <span className="scard">
                    <span className="stoolbar">
                      <span>
                        <i className="slbl">Listen to this lesson</i>
                        <span className="spill">
                          <i className="sp"></i>
                          <i className="st">0:00 / 1:57</i>
                          <i className="str"></i>
                        </span>
                      </span>
                      <span>
                        <i className="slbl">Choose your version</i>
                        <span className="sseg">
                          <i className="on">Standard</i>
                          <i>Personalised</i>
                        </span>
                      </span>
                    </span>
                    <span className="svid">
                      <i className="vcap">
                        Behavioural Science in the real world · Rory Sutherland
                      </i>
                      <i className="vbg">play</i>
                      <i className="vplay"></i>
                      <i className="vbrand">Ogilvy</i>
                      <i className="vlike"></i>
                    </span>
                    <span className="squote">
                      People don't think how they feel, they don't say what they
                      think, and they don't do what they say.
                      <cite>David Ogilvy</cite>
                    </span>
                    <span className="stext">
                      <i className="s1"></i>
                      <i className="s2"></i>
                      <i className="lk" style={{ width: "38%" }}></i>
                      <i className="s1"></i>
                      <i className="lk" style={{ width: "30%" }}></i>
                      <i className="s3"></i>
                    </span>
                    <span className="smodel">
                      <b>Three barriers to action</b>
                      <svg
                        viewBox="0 0 320 200"
                        role="img"
                        aria-label="A triangle showing three behavioural barriers: Scared, Overwhelmed, Lazy."
                      >
                        <polygon
                          points="160,16 296,184 24,184"
                          fill="none"
                          stroke="#2E2437"
                          strokeWidth="2.5"
                          strokeLinejoin="round"
                        ></polygon>
                        <line
                          x1="92"
                          y1="100"
                          x2="228"
                          y2="100"
                          stroke="#2E2437"
                          strokeWidth="1.4"
                          opacity=".5"
                        ></line>
                        <line
                          x1="58"
                          y1="142"
                          x2="262"
                          y2="142"
                          stroke="#2E2437"
                          strokeWidth="1.4"
                          opacity=".5"
                        ></line>
                        <text
                          x="160"
                          y="74"
                          textAnchor="middle"
                          fontFamily="'Open Sans',sans-serif"
                          fontWeight="800"
                          fontSize="15"
                          fill="#2E2437"
                          letterSpacing="1"
                        >
                          SCARED
                        </text>
                        <text
                          x="160"
                          y="92"
                          textAnchor="middle"
                          fontFamily="'Open Sans',sans-serif"
                          fontWeight="600"
                          fontSize="8.5"
                          fill="#5C4F66"
                        >
                          Don't want to commit
                        </text>
                        <text
                          x="160"
                          y="126"
                          textAnchor="middle"
                          fontFamily="'Open Sans',sans-serif"
                          fontWeight="800"
                          fontSize="15"
                          fill="#2E2437"
                          letterSpacing="1"
                        >
                          OVERWHELMED
                        </text>
                        <text
                          x="160"
                          y="138"
                          textAnchor="middle"
                          fontFamily="'Open Sans',sans-serif"
                          fontWeight="600"
                          fontSize="8.5"
                          fill="#5C4F66"
                        >
                          Don't know what to do
                        </text>
                        <text
                          x="160"
                          y="168"
                          textAnchor="middle"
                          fontFamily="'Open Sans',sans-serif"
                          fontWeight="800"
                          fontSize="15"
                          fill="#2E2437"
                          letterSpacing="1"
                        >
                          LAZY
                        </text>
                        <text
                          x="160"
                          y="180"
                          textAnchor="middle"
                          fontFamily="'Open Sans',sans-serif"
                          fontWeight="600"
                          fontSize="8.5"
                          fill="#5C4F66"
                        >
                          Don't want to do the work
                        </text>
                        <circle
                          cx="24"
                          cy="184"
                          r="7"
                          fill="#16a34a"
                          stroke="#2E2437"
                          strokeWidth="1.5"
                        ></circle>
                        <circle
                          cx="296"
                          cy="184"
                          r="7"
                          fill="#dc2626"
                          stroke="#2E2437"
                          strokeWidth="1.5"
                        ></circle>
                      </svg>
                    </span>
                    <span className="sinsights">
                      <span className="sihead">
                        <i className="bulb">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#C4841D"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M9 18h6"></path>
                            <path d="M10 22h4"></path>
                            <path
                              d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2Z"
                              fill="#F3EC7A"
                            ></path>
                          </svg>
                        </i>
                        <b>Your personal insights</b>
                      </span>
                      <i className="silead">
                        Three ways to put this lesson to work in your world this
                        week.
                      </i>
                      <span className="siitem">
                        <i className="num">1</i>
                        <span className="sitext">
                          <i className="b"></i>
                          <i style={{ width: "94%" }}></i>
                        </span>
                      </span>
                      <span className="siitem">
                        <i className="num">2</i>
                        <span className="sitext">
                          <i className="b" style={{ width: "42%" }}></i>
                          <i style={{ width: "88%" }}></i>
                        </span>
                      </span>
                      <span className="siitem">
                        <i className="num">3</i>
                        <span className="sitext">
                          <i className="b" style={{ width: "48%" }}></i>
                          <i style={{ width: "80%" }}></i>
                        </span>
                      </span>
                    </span>
                    <span className="sqcard">
                      <span className="sqrow">
                        <i className="sqbadge">Q</i>
                        <span className="sqtext">
                          <i style={{ width: "96%" }}></i>
                          <i style={{ width: "82%" }}></i>
                        </span>
                      </span>
                      <span className="sqrow">
                        <i className="sqbadge a">A</i>
                        <span className="sqarea"></span>
                      </span>
                      <i className="sqbtn">Submit answer</i>
                    </span>
                  </span>
                </span>
              </span>
              <i className="stag">Story-led video</i>
              <i className="stag t2">Written stories with links</i>
              <i className="stag t3">Graphs &amp; pics to make it stick</i>
              <i className="stag t4">Personalised actionable insights</i>
              <i className="stag t5">A question to land it</i>
            </figure>
          </div>
          <div className="caption">
            <span className="pill">Inside a lesson</span>
            <h4>Bite-sized, story-led, never bone-dry.</h4>
            <p>
              Roughly 15 minutes each. Video, writing with a strong story, and a
              question to make sure the idea has properly landed.
            </p>
          </div>
        </article>

        <article className="proof-card">
          <div className="img-wrap">
            <figure
              className="mock mk-per"
              role="img"
              aria-label="The Deep Thought AI personalisation panel where the learner fills in their name, country, role and goals, then the course rewrites itself around them."
            >
              <span aria-hidden="true">
                <span className="lez bg">
                  <span className="lhero">
                    <i className="leyb">Chapter 2 &nbsp;|&nbsp; Lesson 1</i>
                    <i className="lttl">
                      Why is changing behaviour so difficult?
                    </i>
                    <i className="lmeta">⏱ 20 min</i>
                  </span>
                  <span className="lcard">
                    <span className="lbar">
                      <span className="laud">
                        <i className="lbl">Listen to this lesson</i>
                        <span className="apill">
                          <i className="aply"></i>
                          <i className="atime">0:00 / 1:57</i>
                          <i className="atrack"></i>
                        </span>
                      </span>
                      <span className="lsegwrap">
                        <i className="lbl">Choose your version</i>
                        <span className="lseg">
                          <i className="on">Standard</i>
                          <i className="ai">✨ Personalised</i>
                        </span>
                      </span>
                    </span>
                    <span className="lvid">
                      <i className="vcap">
                        Behavioural Science in the real world · Rory Sutherland
                      </i>
                      <i className="vbg">play</i>
                      <i className="vplay"></i>
                      <i className="vbrand">Ogilvy</i>
                      <i className="vlike"></i>
                    </span>
                    <span className="lquote">
                      People don't think how they feel, they don't say what they
                      think, and they don't do what they say.
                      <cite>David Ogilvy</cite>
                    </span>
                  </span>
                </span>
                <i className="dim"></i>
                <span className="after lez">
                  <span className="lhero">
                    <i className="leyb">Chapter 2 &nbsp;|&nbsp; Lesson 1</i>
                    <i className="lttl">
                      Why is changing behaviour so difficult?
                    </i>
                    <i className="lmeta">⏱ 20 min</i>
                  </span>
                  <span className="lcard">
                    <span className="lbar">
                      <span className="laud">
                        <i className="lbl">Listen to this lesson</i>
                        <span className="apill">
                          <i className="aply"></i>
                          <i className="atime">0:00 / 1:57</i>
                          <i className="atrack"></i>
                        </span>
                      </span>
                      <span className="lsegwrap">
                        <i className="lbl">Choose your version</i>
                        <span className="lseg">
                          <i>Standard</i>
                          <i className="on ai">✨ Personalised</i>
                        </span>
                      </span>
                    </span>
                    <span className="lchip">
                      <i className="spark">✨</i>
                      <span>
                        Rewritten by Deep Thought AI around your goals:
                        leadership and brand building.{" "}
                        <b>All data, frameworks and facts kept intact.</b>
                      </span>
                    </span>
                    <span className="lvid">
                      <i className="vcap">
                        Behavioural Science in the real world · Rory Sutherland
                      </i>
                      <i className="vbg">play</i>
                      <i className="vplay"></i>
                      <i className="vbrand">Ogilvy</i>
                      <i className="vlike"></i>
                    </span>
                    <span className="lquote">
                      People don't think how they feel, they don't say what they
                      think, and they don't do what they say.
                      <cite>David Ogilvy</cite>
                    </span>
                  </span>
                </span>
                <span className="pzm">
                  <i className="pzx">✕</i>
                  <h5>Personalise this course</h5>
                  <p className="pzsub">
                    Tell <b>Deep Thought AI</b> how to tailor your course
                    content to you.
                  </p>
                  <p className="madlib">
                    I’m{" "}
                    <span className="blank">
                      <span className="type t1">William</span>
                    </span>{" "}
                    from{" "}
                    <span className="blank">
                      <span className="type t2">the UK</span>
                    </span>
                    . I’m currently{" "}
                    <span className="blank">
                      <span className="type t3">the King</span>
                    </span>{" "}
                    and I’m here to level up my skills in{" "}
                    <span className="blank">
                      <span className="type t4">
                        leadership and brand building
                      </span>
                    </span>
                    .
                  </p>
                  <i className="pbtn">Personalise my course ✨</i>
                </span>
                <span className="pzload">
                  <h5>Creating amazing content for you</h5>
                  <span className="quipwrap">
                    <i className="quip">
                      Teaching Deep Thought about your goals
                    </i>
                    <i className="quip q2">
                      Rewriting the examples around your job
                    </i>
                  </span>
                  <span className="barrow">
                    <span className="track">
                      <i className="fill"></i>
                    </span>
                    <span className="pcts">
                      <i>14%</i>
                      <i className="p2">52%</i>
                      <i className="p3">96%</i>
                    </span>
                  </span>
                </span>
              </span>
            </figure>
          </div>
          <div className="caption">
            <span className="pill">AI personalisation</span>
            <h4>Tailored to your role and goals.</h4>
            <p>
              Tell us what you do and what you're trying to fix. Our AI rewrites
              every lesson with practical takeouts for your specific job.
              Frameworks and data remain intact; the examples become yours.
            </p>
          </div>
        </article>

        <article className="proof-card">
          <div className="img-wrap">
            <img
              className="fb-shot"
              src={require("../../../../../assets/images/courses/feedback.png")}
              alt="Written feedback from a 42courses reviewer on a learner's health-check answer, shown beside the original question."
              loading="lazy"
              decoding="async"
              width="793"
              height="496"
            />
          </div>
          <div className="caption">
            <span className="pill">Real feedback</span>
            <h4>A human in your corner.</h4>
            <p>
              Select questions are manually marked by a real person on our team.
              If you need a steer, you'll get one. Stuck? We're happy to hop on
              a call.
            </p>
          </div>
        </article>
        <article className="proof-card">
          <div className="img-wrap">
            <figure
              className="mock mk-cert"
              role="img"
              aria-label="A CPD-accredited Mini MBA certificate in a gold-lined frame, hanging on a wall."
            >
              <span aria-hidden="true">
                <span className="cstring">
                  <svg viewBox="0 0 100 24" preserveAspectRatio="none">
                    <path
                      d="M50 2 L7 23"
                      fill="none"
                      stroke="#2E2437"
                      strokeWidth=".7"
                    ></path>
                    <path
                      d="M50 2 L93 23"
                      fill="none"
                      stroke="#2E2437"
                      strokeWidth=".7"
                    ></path>
                  </svg>
                  <i className="nail"></i>
                </span>
                <span className="frame">
                  <span className="fillet">
                    <span className="matb">
                      <span className="paper">
                        <span className="clogo">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="4 37 596 158"
                            role="img"
                            aria-label="42courses"
                          >
                            <path
                              fill="#2E2437"
                              d="M214.32,123.21c0,16,11.42,28.19,27.06,28.19,16.77,0,24.7-11.83,25.73-21h-15a10.84,10.84,0,0,1-10.8,8c-7.82,0-12.35-6.17-12.35-15.22S233.46,108,241.28,108a10.84,10.84,0,0,1,10.8,8h15c-1-9.16-9-21-25.73-21C225.74,95,214.32,107.15,214.32,123.21Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M300.6,151.4c17.29,0,28.3-12.86,28.3-28.19S317.89,95,300.6,95s-28.3,12.86-28.3,28.2S283.31,151.4,300.6,151.4Zm-13.69-28.19c0-9.68,6-15.34,13.69-15.34s13.68,5.66,13.68,15.34-6,15.33-13.68,15.33S286.91,132.88,286.91,123.21Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M362.88,151.4c15.74,0,24.49-6.79,24.49-24.28V96.25H372.76v31.07c0,7.51-3.4,11-9.88,11s-9.88-3.5-9.88-11V96.25H338.39v30.87C338.39,144.61,347.14,151.4,362.88,151.4Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M430.66,95.83s-1.23-.1-1.75-.1c-9.16,0-13.27,4.32-15.23,7.51v-7H400.2v53.92h14.62V123.31c0-9.88,4.42-14.1,13-14.1a17.22,17.22,0,0,1,2.88.31Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M457.23,151.4c14.61,0,22.84-7.61,22.84-17.29,0-22.12-29.84-13.07-29.84-22.12,0-3,2.67-5.25,7.41-5.25,5.25,0,7.82,3.09,8.13,6.48h14.09c-.4-7.92-6-18.21-22.12-18.21-13.27,0-21.61,8-21.61,17.8,0,21.41,29.95,12.86,29.95,21.92,0,3-2.89,4.94-8.44,4.94-6.07,0-8.75-3.4-8.95-7H433.87C434.59,142.76,440.35,151.4,457.23,151.4Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M539.86,127.42s.2-2,.2-4.32c0-15.95-10.8-28.09-26.75-28.09s-27.07,12.14-27.07,28.09c0,16.57,11.22,28.3,27.17,28.3,12.45,0,22.33-7.51,25.52-18.42H524.21c-1.75,3.91-6,5.87-10.39,5.87-7.3,0-12-4-13.07-11.43ZM513.2,106.74c6.79,0,11.22,4.12,12.25,10.19H501C502.19,110.75,506.52,106.74,513.2,106.74Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M569.07,151.4c14.61,0,22.85-7.61,22.85-17.29,0-22.12-29.84-13.07-29.84-22.12,0-3,2.67-5.25,7.4-5.25,5.25,0,7.82,3.09,8.13,6.48h14.1c-.41-7.92-6-18.21-22.12-18.21-13.28,0-21.61,8-21.61,17.8,0,21.41,29.94,12.86,29.94,21.92,0,3-2.88,4.94-8.44,4.94-6.07,0-8.74-3.4-9-7H545.71C546.44,142.76,552.19,151.4,569.07,151.4Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
                            ></path>
                            <path
                              fill="#2E2437"
                              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
                            ></path>
                            <path
                              fill="#fff"
                              d="M68.9,149.18H80.76v-8.11h6.69v-10.2H80.76V97.61h-9.6l-23,36v7.43H68.9ZM61,131.54l8.86-14.71v14.71Z"
                            ></path>
                            <path
                              fill="#fff"
                              d="M108.51,138.22v-.15l8-8.33c6.83-7.13,8.33-11.56,8.33-17.42,0-9.61-7.81-15.39-16.67-15.39s-14.48,4.43-17.56,9.76l8.63,6.23c2.25-3.3,4.88-4.8,8-4.8a5.32,5.32,0,0,1,5.4,5.55c0,3.68-2.32,6.91-6.38,11.19L91,141.3l.15,7.88h34.24v-11Z"
                            ></path>
                          </svg>
                        </span>
                        <span className="mrow">
                          <i></i>
                          <b>Mini MBA</b>
                          <i></i>
                        </span>
                        <i className="ctitle" id="mock-cert-course">
                          {courseName || "Mini MBA"}
                        </i>
                        <i className="cname">Your name here</i>
                        <i className="csub">
                          Eight hours of curiosity, well spent and CPD
                          accredited.
                        </i>
                        <span className="crow">
                          <span className="sig">
                            <svg
                              viewBox="0 0 100 20"
                              preserveAspectRatio="none"
                            >
                              <path
                                d="M2 14 C 12 2, 20 18, 30 10 S 50 2, 58 12 S 80 16, 98 6"
                                fill="none"
                                stroke="#2E2437"
                                strokeWidth="2"
                                strokeLinecap="round"
                              ></path>
                            </svg>
                            <i></i>
                            <em>Course Director</em>
                          </span>
                          <span className="bseal">
                            <svg
                              viewBox="0 0 100 100"
                              role="img"
                              aria-label="42courses achievement seal"
                            >
                              <path
                                d="M50.00,3.00 L53.57,9.16 L58.16,3.71 L60.61,10.40 L66.07,5.83 L67.33,12.84 L73.50,9.30 L73.52,16.41 L80.21,14.00 L78.99,21.01 L86.00,19.79 L83.59,26.48 L90.70,26.50 L87.16,32.67 L94.17,33.93 L89.60,39.39 L96.29,41.84 L90.84,46.43 L97.00,50.00 L90.84,53.57 L96.29,58.16 L89.60,60.61 L94.17,66.07 L87.16,67.33 L90.70,73.50 L83.59,73.52 L86.00,80.21 L78.99,78.99 L80.21,86.00 L73.52,83.59 L73.50,90.70 L67.33,87.16 L66.07,94.17 L60.61,89.60 L58.16,96.29 L53.57,90.84 L50.00,97.00 L46.43,90.84 L41.84,96.29 L39.39,89.60 L33.93,94.17 L32.67,87.16 L26.50,90.70 L26.48,83.59 L19.79,86.00 L21.01,78.99 L14.00,80.21 L16.41,73.52 L9.30,73.50 L12.84,67.33 L5.83,66.07 L10.40,60.61 L3.71,58.16 L9.16,53.57 L3.00,50.00 L9.16,46.43 L3.71,41.84 L10.40,39.39 L5.83,33.93 L12.84,32.67 L9.30,26.50 L16.41,26.48 L14.00,19.79 L21.01,21.01 L19.79,14.00 L26.48,16.41 L26.50,9.30 L32.67,12.84 L33.93,5.83 L39.39,10.40 L41.84,3.71 L46.43,9.16 Z"
                                fill="#C4841D"
                              ></path>
                              <circle
                                cx="50"
                                cy="50"
                                r="40"
                                fill="#F5B041"
                              ></circle>
                              <circle
                                cx="50"
                                cy="50"
                                r="40"
                                fill="none"
                                stroke="#C4841D"
                                strokeWidth="1"
                              ></circle>
                              <circle
                                cx="50"
                                cy="50"
                                r="35"
                                fill="none"
                                stroke="#C4841D"
                                strokeWidth="1.4"
                              ></circle>
                              <circle
                                cx="87.50"
                                cy="50.00"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="86.56"
                                cy="58.34"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="83.79"
                                cy="66.27"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="79.32"
                                cy="73.38"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="73.38"
                                cy="79.32"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="66.27"
                                cy="83.79"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="58.34"
                                cy="86.56"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="50.00"
                                cy="87.50"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="41.66"
                                cy="86.56"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="33.73"
                                cy="83.79"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="26.62"
                                cy="79.32"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="20.68"
                                cy="73.38"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="16.21"
                                cy="66.27"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="13.44"
                                cy="58.34"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="12.50"
                                cy="50.00"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="13.44"
                                cy="41.66"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="16.21"
                                cy="33.73"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="20.68"
                                cy="26.62"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="26.62"
                                cy="20.68"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="33.73"
                                cy="16.21"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="41.66"
                                cy="13.44"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="50.00"
                                cy="12.50"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="58.34"
                                cy="13.44"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="66.27"
                                cy="16.21"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="73.38"
                                cy="20.68"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="79.32"
                                cy="26.62"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="83.79"
                                cy="33.73"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <circle
                                cx="86.56"
                                cy="41.66"
                                r="0.7"
                                fill="#C4841D"
                              ></circle>
                              <ellipse
                                cx="40"
                                cy="36"
                                rx="20"
                                ry="11"
                                fill="#fff"
                                opacity="0.18"
                              ></ellipse>
                              <g transform="translate(33.5,34) scale(0.165)">
                                <path
                                  fill="#2E2437"
                                  d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z"
                                ></path>
                                <path
                                  fill="#2E2437"
                                  d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z"
                                ></path>
                                <path
                                  fill="#fff"
                                  d="M70.59,137.36H84.13v-9.25h7.62V116.46H84.13V78.51h-11l-26.3,41.12v8.48H70.59Zm-9-20.13,10.11-16.79v16.79Z"
                                ></path>
                                <path
                                  fill="#fff"
                                  d="M115.8,124.85v-.17l9.16-9.51c7.8-8.13,9.51-13.19,9.51-19.87,0-11-8.91-17.56-19-17.56s-16.53,5.05-20,11.13L105.26,96c2.57-3.77,5.57-5.48,9.08-5.48a6.08,6.08,0,0,1,6.17,6.34c0,4.2-2.66,7.88-7.28,12.77l-17.4,18.76.18,9h39.06V124.85Z"
                                ></path>
                              </g>
                            </svg>
                          </span>
                        </span>
                      </span>
                    </span>
                  </span>
                </span>
              </span>
            </figure>
          </div>
          <div className="caption">
            <span className="pill">The certificate</span>
            <h4>A Mini MBA you can hang up.</h4>
            <p>
              CPD-accredited, signed, dated and trackable. Our completion rate
              sits above 80% against an industry average closer to 10%.
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>
);

export default Proof;
